import {
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableState,
} from "@tanstack/react-table";
import { useEffect, useMemo } from "react";
import { ArrayParam, useQueryParam, withDefault } from "use-query-params";

import useDataTable, {
  type DataTableOptions,
  type ReactTableOptions,
} from "common/components/DataTable/useDataTable";
import usePagination from "common/hooks/usePagination";

interface UsePaginatedDataTableOptions<TData extends { id: string }, TValue> {
  reactTableOptions: ReactTableOptions<TData, TValue>;
  // localStorageId is required here — it keys the persisted page size
  dataTableOptions: DataTableOptions<TData> & { localStorageId: string };
  isLoading: boolean;
}

export default function usePaginatedDataTable<
  TData extends { id: string },
  TValue,
>({
  reactTableOptions,
  dataTableOptions,
  isLoading,
}: UsePaginatedDataTableOptions<TData, TValue>) {
  const { page, setPage, pageSize, setPageSize } = usePagination(
    dataTableOptions.localStorageId,
  );

  const [sort, setSort] = useQueryParam("sort", withDefault(ArrayParam, []), {
    removeDefaultsFromUrl: true,
    updateType: "replaceIn",
  });

  const sorting: SortingState = useMemo(
    () =>
      sort
        .map((sortItem: string) => {
          const [id, order] = sortItem.split(":");
          return { id, desc: order === "desc" };
        })
        .filter(({ id }) => id),
    [sort],
  );

  const state: Partial<TableState> = {
    sorting,
    pagination: { pageIndex: isLoading ? 0 : page, pageSize },
  };

  const tableOptions: ReactTableOptions<TData, TValue> = {
    ...reactTableOptions,
    state,
    manualPagination: false,
    data:
      isLoading || !reactTableOptions.data
        ? new Array(pageSize).fill({})
        : reactTableOptions.data,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: (updaterOrValue) => {
      const newPagination =
        typeof updaterOrValue === "function"
          ? updaterOrValue({ pageIndex: page, pageSize })
          : updaterOrValue;

      setPage(newPagination.pageIndex);
      setPageSize(newPagination.pageSize);
    },
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sorting)
          : updaterOrValue;

      const sortArray = newSorting.map(
        ({ id, desc }) => `${id}:${desc ? "desc" : "asc"}`,
      );

      setSort(sortArray);
    },
    autoResetPageIndex: false,
  };

  const table = useDataTable<TData, TValue>(tableOptions, dataTableOptions);

  const { getRowModel, getRowCount, firstPage } = table;
  const dataLength = reactTableOptions.data?.length ?? 0;

  useEffect(() => {
    if (getRowModel().rows.length === 0 && getRowCount() !== 0 && !isLoading) {
      firstPage();
    }
  }, [firstPage, getRowCount, getRowModel, isLoading, dataLength]);

  return { table };
}
